<template>
  <div class="rigid-body-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="main-layout vertical">
      <!-- 混合渲染区 -->
      <div class="canvas-mixed">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- 路面 -->
           <line x1="0" y1="200" x2="500" y2="200" stroke="rgba(255,255,255,0.1)" stroke-width="2" />
           
           <!-- 轮子组合 -->
           <g :transform="`translate(${combinedX}, 160)`">
              <!-- 轮子主体 -->
              <circle r="40" fill="none" stroke="#4da1ff" stroke-width="4" />
              <g :transform="`rotate(${rotationAngle})`">
                 <!-- 辐条 -->
                 <line v-for="i in 8" :key="i" :x1="0" :y1="0" :x2="40 * Math.cos(i*Math.PI/4)" :y2="40 * Math.sin(i*Math.PI/4)" stroke="rgba(77, 161, 255, 0.3)" />
                 <!-- 边缘观察点 P -->
                 <circle cx="40" cy="0" r="5" fill="#ff4757" />
                 <text x="48" y="5" fill="#ff4757" font-size="10">P</text>
              </g>

              <!-- 质心 C -->
              <circle r="4" fill="#fff" />
              <text y="-10" text-anchor="middle" fill="#fff" font-size="10">C (质心)</text>

              <!-- 速度矢量 -->
              <line v-if="showTranslation" x1="0" y1="0" x2="30" y2="0" stroke="#fff" stroke-width="2" marker-end="url(#arr-v)" />
              <line v-if="showRotation" x1="40" y1="0" x2="40" y2="30" stroke="#ff4757" stroke-width="2" marker-end="url(#arr-v)" />
           </g>

           <!-- 轨迹线条 (摆线) -->
           <path v-if="showRotation" :d="cycloidPath" fill="none" stroke="#ff4757" stroke-width="1.5" stroke-dasharray="3,3" opacity="0.6" />
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>滚动速度 (v): {{ speed }}</label>
                  <el-slider v-model="speed" :min="1" :max="10" size="mini"></el-slider>
               </div>
               <div class="col bottom-flex">
                  <el-button type="primary" size="mini" @click="reset">回到起点</el-button>
               </div>
            </div>
         </div>

         <div class="concept-note">
            <h5>物理原理解析：</h5>
            <p>1. <strong>平动</strong>：质心C沿直线运动，物体上各点速度相同。</p>
            <p>2. <strong>转动</strong>：物体绕质心轴转动，边缘点P的速度方向不断改变。</p>
            <p>3. <strong>叠加效应</strong>：边缘点的实际速度是平动速度与转动线速度的<strong>矢量合成</strong>。</p>
         </div>
      </div>
    </div>

    <defs>
      <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="#fff" /> </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'RigidBodyViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      speed: 3,
      time: 0,
      showTranslation: true,
      showRotation: true,
      running: true,
      cycloidPoints: []
    }
  },
  computed: {
    combinedX() { return (this.time * this.speed * 10) % 550 - 50; },
    rotationAngle() { 
      // 纯滚动条件: v = ωR. 
      return (this.time * this.speed * 10 / 40) * 180 / Math.PI; 
    },
    cycloidPath() {
       let d = "";
       this.cycloidPoints.forEach((p, i) => {
          d += (i === 0 ? "M" : "L") + ` ${p.x} ${p.y}`;
       });
       return d;
    }
  },
  methods: {
    reset() { 
      this.time = 0; 
      this.cycloidPoints = [];
    },
    animate() {
      if (!this.running) return;
      this.time += 0.05;
      
      // Calculate P point global position for cycloid
      const angle = (this.time * this.speed * 10 / 40);
      const px = this.combinedX + 40 * Math.cos(angle);
      const py = 160 + 40 * Math.sin(angle);
      
      this.cycloidPoints.push({ x: px, y: py });
      if (this.cycloidPoints.length > 100) this.cycloidPoints.shift();
      if (this.combinedX < -40) this.cycloidPoints = [];

      requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { this.running = false; }
}
</script>

<style scoped lang="scss">
.rigid-body-viz-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .switches { display: flex; gap: 15px; }
}

::v-deep .el-checkbox__label { color: #888; font-size: 0.75rem; }
::v-deep .el-checkbox__inner { background: transparent; border-color: #4da1ff; }

.canvas-mixed {
  flex: 1.2; background: rgba(0,0,0,0.3); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05);
}

.bottom-controls {
  flex: 0.8; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-bottom: 10px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 15px;
  .row { display: grid; grid-template-columns: 1fr auto; gap: 20px; align-items: center; }
  .col { label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 5px; } }
}

.concept-note {
  background: rgba(77, 161, 255, 0.05); border-radius: 12px; padding: 15px; border-left: 3px solid #4da1ff;
  h5 { margin: 0 0 8px 0; color: #4da1ff; font-size: 0.85rem; }
  p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } }
}
</style>
